import { useLocale, useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Checkbox } from '@multaqa/ui/components/ui/checkbox'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { Label } from '@multaqa/ui/components/ui/label'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { Spinner } from '@multaqa/ui/components/ui/spinner'
import { useState } from 'react'
import { type DataImportSection, downloadImportTemplate } from './api'

/** Pick the sheets a company needs and download the template with them. */
export function DataImportTemplateDialog({
  groupId,
  companyId,
  sections,
  open,
  onOpenChange,
}: {
  groupId: string
  companyId: string
  sections: DataImportSection[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [chosen, setChosen] = useState<string[]>(() => sections.map((section) => section.key))
  const [busy, setBusy] = useState(false)

  const toggle = (key: string, on: boolean) =>
    setChosen((current) => (on ? [...current, key] : current.filter((item) => item !== key)))

  const download = async () => {
    setBusy(true)
    try {
      await downloadImportTemplate(
        groupId,
        companyId,
        sections.map((section) => section.key).filter((key) => chosen.includes(key)),
        locale,
      )
      onOpenChange(false)
    } catch {
      toast.error(t('companies.imports.templateFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('action.cancel')} data-testid="data-import-template-dialog">
        <DialogHeader>
          <DialogTitle>{t('companies.imports.templateTitle')}</DialogTitle>
          <DialogDescription>{t('companies.imports.templateDescription')}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <ul className="flex flex-col gap-1">
            {sections.map((section) => (
              <li key={section.key} className="flex items-start gap-3 rounded-sm px-1 py-2">
                <Checkbox
                  id={`data-import-section-${section.key}`}
                  checked={chosen.includes(section.key)}
                  onCheckedChange={(value) => toggle(section.key, value === true)}
                />
                <Label
                  htmlFor={`data-import-section-${section.key}`}
                  className="flex flex-col items-start gap-0.5"
                >
                  <span>{section.label}</span>
                  <span className="text-small font-normal text-muted-foreground">
                    {section.columns
                      .map((column) => column.label)
                      .slice(0, 4)
                      .join(' · ')}
                    {section.columns.length > 4 ? ' …' : ''}
                  </span>
                </Label>
              </li>
            ))}
          </ul>
          {chosen.length === 0 ? (
            <p className="text-small text-danger">{t('companies.imports.templateNone')}</p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            disabled={chosen.length === 0 || busy}
            onClick={() => void download()}
          >
            {busy ? <Spinner className="size-4" /> : null}
            {t('companies.imports.templateDownload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
